import { useState } from 'react';
import {
    Alert, Box, Button, Card, CircularProgress, FormControl, FormLabel,
    Input, LinearProgress, Option, Select, Sheet, Stack, Typography,
} from '@mui/joy';

import { useAdmin } from '../../../AdminContext';
import { APIAdminAdsTerritoryCapacity, adminAdsError } from '../../../api_admin_ads';
import { formatInt } from '../../../components/Ads/adsConstants';

function Metric({ label, value, suffix }) {
    return (
        <Card variant="soft" sx={{ flex: 1, minWidth: 130, p: 1.5 }}>
            <Typography level="body-xs" color="neutral">{label}</Typography>
            <Typography level="h4">{value}{suffix || ''}</Typography>
        </Card>
    );
}

export default function AdminAdsCapacityPage() {
    const { adminToken: token } = useAdmin();

    const [mode, setMode] = useState('city'); // city | region
    const [name, setName] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = async () => {
        if (!name.trim()) { setError('Укажите название'); return; }
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const params = mode === 'city' ? { city: name.trim() } : { region: name.trim() };
            const res = await APIAdminAdsTerritoryCapacity(token, params);
            setData(res);
        } catch (e) {
            setError(adminAdsError(e, 'Не удалось получить ёмкость'));
        } finally {
            setLoading(false);
        }
    };

    const cap = data?.capacity;
    const slots = data?.slots || [];
    const maxSlot = Math.max(1, ...slots.map((s) => s.active_campaigns));

    return (
        <Box sx={{ maxWidth: 860 }}>
            <Typography level="h4" sx={{ mb: 0.5 }}>Ёмкость территории</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Сколько показов территория даёт за сутки, сколько уже занято и на какие часы
                приходится пик спроса.
            </Typography>

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-end">
                    <FormControl>
                        <FormLabel>Тип</FormLabel>
                        <Select value={mode} onChange={(_, v) => setMode(v)} sx={{ minWidth: 140 }}>
                            <Option value="city">Город</Option>
                            <Option value="region">Регион</Option>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                        <FormLabel>{mode === 'city' ? 'Город' : 'Регион'}</FormLabel>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                               onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
                               placeholder={mode === 'city' ? 'Москва' : 'Московская область'} />
                    </FormControl>
                    <Button loading={loading} onClick={load}>Показать</Button>
                </Stack>
            </Sheet>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>}

            {cap && (
                <>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
                        <Metric label="Инвентарь/сут" value={formatInt(cap.available_per_day)} />
                        <Metric label="Доступно с маржой" value={formatInt(cap.usable_per_day)} />
                        <Metric label="Занято/сут" value={formatInt(cap.committed_per_day)} />
                        <Metric label="Свободно/сут" value={formatInt(cap.free_per_day)} />
                    </Stack>

                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography level="body-sm">Заполняемость</Typography>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>{cap.fill_percent}%</Typography>
                        </Stack>
                        <LinearProgress determinate value={Math.min(100, cap.fill_percent)}
                                        color={cap.fill_percent >= 100 ? 'danger' : cap.fill_percent >= 70 ? 'warning' : 'success'} />
                        <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                            Защитная маржа: {Math.round((cap.safety_margin || 0) * 100)}%.
                            {cap.fill_percent >= 100
                                ? ' Свободного трафика нет — новую рекламу территория не потянет.'
                                : cap.fill_percent >= 70
                                    ? ' Территория загружена, запас небольшой.'
                                    : ' Есть свободный трафик.'}
                        </Typography>
                    </Sheet>

                    <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md' }}>
                        <Typography level="title-sm" sx={{ mb: 1.5 }}>Занятость по часам (кампаний претендует)</Typography>
                        {slots.length === 0 ? (
                            <Typography level="body-sm" color="neutral">Нет данных по часам.</Typography>
                        ) : (
                            <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ height: 120 }}>
                                {slots.map((s) => (
                                    <Box key={s.hour} sx={{ flex: 1, textAlign: 'center' }}>
                                        <Box sx={{
                                            height: `${(s.active_campaigns / maxSlot) * 96}px`,
                                            minHeight: s.active_campaigns ? 4 : 0,
                                            bgcolor: 'primary.softBg',
                                            borderRadius: 'xs',
                                        }} title={`${s.hour}:00 — ${s.active_campaigns}`} />
                                        {s.hour % 3 === 0 && (
                                            <Typography level="body-xs" color="neutral">{s.hour}</Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Sheet>
                </>
            )}
        </Box>
    );
}